/**
 * Israeli phone number utilities — single source of truth. The legacy code
 * has multiple inconsistent normalize implementations (some convert `+972` to `0`
 * inconsistently, some strip plus, some don't).
 *
 * Canonical local form: 10 digits starting with `05` (mobile) or 9 digits
 * starting with `0` (landline). The `normalize()` function always returns the
 * leading-zero local form so it can be used as a dictionary key consistently.
 */

/**
 * Convert any reasonable Israeli phone representation to the canonical local
 * form (leading `0`). Returns the digits-only string; the caller can validate
 * with `isValidMobile()` or `isValid()`.
 *
 *   `+972-50-555-8022` → `0505558022`
 *   `972505558022`     → `0505558022`
 *   `050-555-8022`     → `0505558022`
 *   `0505558022`       → `0505558022`
 *
 * @param {unknown} input
 * @returns {string}
 */
export function normalize(input) {
  let s = String(input ?? '').replace(/\D/g, '');
  if (s.startsWith('972')) s = '0' + s.slice(3);
  if (s.length > 0 && !s.startsWith('0')) s = '0' + s;
  return s;
}

/**
 * Convert to E.164 international form (with `+972` prefix). Returns empty
 * string for invalid input.
 * @param {unknown} input
 * @returns {string}
 */
export function toE164(input) {
  const n = normalize(input);
  if (!n.startsWith('0') || n.length < 9) return '';
  return '+972' + n.slice(1);
}

/**
 * @param {unknown} input
 * @returns {boolean} true if normalize(input) is a 10-digit mobile (`05X...`)
 */
export function isValidMobile(input) {
  const n = normalize(input);
  return /^05\d{8}$/.test(n);
}

/**
 * Generic validity check: 9- or 10-digit Israeli number starting with 0.
 * @param {unknown} input
 * @returns {boolean}
 */
export function isValid(input) {
  const n = normalize(input);
  return /^0\d{8,9}$/.test(n);
}

/**
 * Display formatter: `050-555-8022`.
 * @param {unknown} input
 * @returns {string}
 */
export function format(input) {
  const n = normalize(input);
  if (n.length === 10 && n.startsWith('05')) {
    return `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}`;
  }
  if (n.length === 9) {
    return `${n.slice(0, 2)}-${n.slice(2, 5)}-${n.slice(5)}`;
  }
  return n;
}
